/*
Usage Notes: 

The following web component can be dropped into any standard Shopify theme (i.e. non-headless), using Liquid to pass product data:

  <skio-plan-picker 
    product='{{ product | json | escape }}'
    selectedVariant='{{ product.selected_or_first_available_variant | json | escape }}'
    formId='{{ product_form_id }}'
    >
  </skio-plan-picker>
  <script src="{{ 'skio-plan-picker-component.js' | asset_url }}" type="module"></script>

  Note: formId not required if element is inside of a form already
*/

import { LitElement, html, css } from 'https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js';
// LitJS Core https://cdn.jsdelivr.net/gh/lit/dist@2/core/lit-core.min.js
// LitJS ALL (includes directives) https://cdn.jsdelivr.net/gh/lit/dist@2/all/lit-all.min.js

const skioStyles = css`
  .skio-plan-picker {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 0;
    border: 0;
    margin-top: 1rem;
  }
  .skio-onetime-second {
    order: 2;
  }
  
  .skio-group-container {
    display: none;
  }

  .skio-group-container--available {
    display: block;
    position: relative;
    /* box-shadow: 0 0 5px rgba(23, 24, 24, 0.05), 0 1px 2px rgba(0, 0, 0, 0.07);
    border-radius: 5px;
    border-width: 1px;
    border-color: transparent;
    border-style: solid; 
    transition: border-color 0.2s ease; */
  }

  /* .skio-group-container--selected {
    border-color: #000;
  } */
  
  .skio-group-input {
    position: absolute;
    width: 0px;
    height: 0px;
    opacity: 0;
  }
  
  .skio-group-wrapper {
    display: flex;
    flex-direction: column;
    cursor: pointer;
    padding: 10px;
    overflow: hidden;
  }
  
  .skio-group-topline {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    width: 100%;
    font-size: 16px;
    gap: 10px;
  }

  .skio-group-label:hover .skio-radio {
    transform: scale(1);
    opacity: 0.75;
  }

  .skio-group-container--selected .skio-group-label .skio-radio {
    transform: scale(1);
    opacity: 1;
  }
  
  .skio-price {
    margin-left: auto;
  }
  
  .skio-group-content {
    width: auto;
    transition: max-height 0.25s cubic-bezier(0.4,0,0.2,1),
                opacity 0.25s cubic-bezier(0.4,0,0.2,1);
    max-height: 60px;
    opacity: 1;
    margin: 1rem 0 1rem 50px;
  }

  .skio-group-content h6 {
    margin-top: 0;
    font-size: 16px;
    text-transform: uppercase;
    color: #000;
    margin-bottom: 6px;
    font-family: inherit;
    font-weight: 400;
  }
  
  /* Hide frequency if not selected */
  .skio-group-container:not(.skio-group-container--selected) .skio-group-content {
    max-height: 0;
    opacity: 0;
    pointer-events: none;
    margin-bottom: 0;
  }
  
  .skio-group-title {
    min-width: max-content;
    font-size: 18px;
    text-transform: uppercase;
    color: #000;

    font-family: inherit;
  }

  /* The switch - the box around the slider */
  .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 24px;
  }

  /* Hide default HTML checkbox */
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  /* The slider */
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    -webkit-transition: .4s;
    transition: .4s;
  }

  .slider:before {
    position: absolute;
    content: "";
    height: 16px;
    width: 16px;
    left: 4px;
    bottom: 4px;
    background-color: white;
    -webkit-transition: .4s;
    transition: .4s;
  }

  input:checked + .slider {
    background-color: #dd536d;
  }

  input:focus + .slider {
    box-shadow: 0 0 1px #dd536d;
  }

  input:checked + .slider:before {
    -webkit-transform: translateX(16px);
    -ms-transform: translateX(16px);
    transform: translateX(16px);
  }

  /* Rounded sliders */
  .slider.round {
    border-radius: 34px;
  }

  .slider.round:before {
    border-radius: 50%;
  }

  .skio-button-selector-wrapper {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .skio-button-selector input {
    display: none;
  }

  .skio-button-selector label { 
    padding: 3px 5px;
    border-radius: 5px;
    background: #f9f9f9;
    border: 1px solid #babbbc;
    cursor: pointer;
    font-size: 13px;
    transition: all 0.25s ease;
  }

  .skio-button-selector label:hover { 
    background-color: #dd536d;
    color: white;
  }

  .skio-button-selector input:checked ~ label {
    background-color: #dd536d;
    color: white;
    border: 1px solid transparent;
  }
`;

export class SkioPlanPickerComponent extends LitElement {
  static properties = {
    product: { type: Object },            //required
    productHandle: { type: String },      //optional (unless product isn't passed, then required)
    key: { type: String },                //optional, defaults to product.id; identifier for this instance of the Skio plan picker
    formId: { type: String },

    subscriptionFirst: { type: Boolean }, //optional, defaults to false; if true, shows subscription option above onetime
    startSubscription: { type: Boolean }, //optional, defaults to false; if true, auto-selects subscription on page load
    hideSelect: { type: Boolean }, // optional, defaults to false; if true, hides the select dropdown
    discountFormat: { type: String },     //optional, defaults to percent; can also pass "fixed"
    
    
    externalPriceSelector: { type: String },      //optional, used to update the external price
    externalPriceSelectorWithCurrency: { type: String },      //optional, used to update the external price
    
    currency: { type: String },           //optional, defaults to 'USD', but can pass any 3 char identifier
    language: { type: String },           //optional, defaults to 'en-US', but can pass any similarly formatted language identifier
    moneyFormatter: {},                   //placeholder for object
  
    selectedVariant: { type: Object },    //placeholder for data
    skioSellingPlanGroups: {},            //placeholder for data
    availableSellingPlanGroups: {},       //placeholder for data
    selectedSellingPlanGroup: {},         //placeholder for data
    selectedSellingPlan: {},              //placeholder for data
  };

  static styles = skioStyles;

  constructor() {
    super();
    
    this.product = null;
    this.selectedVariant = null;

    this.productHandle = null;

    this.key = null;
    this.formId = null;

    this.skioSellingPlanGroups = null;
    this.availableSellingPlanGroups = null;

    this.selectedSellingPlanGroup = null;
    this.selectedSellingPlan = null;

    this.startSubscription = false;
    this.subscriptionFirst = false;
    this.hideSelect = false;

    this.skioMainProduct = true;

    this.discountFormat = 'percent';
    
    this.externalPriceSelector = '[skio-external-price]';
    this.externalPriceSelectorWithCurrency = '[skio-external-price-with-currency]';

    this.currency = window?.Shopify?.currency?.active || 'USD';
    this.language = window?.Shopify?.locale || 'en-US';
    this.moneyFormatter = new Intl.NumberFormat(this.language, {
      style: 'currency',
      currency: this.currency,
    });
  }

  connectedCallback() {
    super.connectedCallback();

    if(!this.product && this.productHandle) {
      this.fetchProduct(this.productHandle);
    }

    this.bindVariantChange();
  }

  render() {
    if(!this.product || !this.selectedVariant) return;
    
    return html`
      <fieldset class="skio-plan-picker" skio-plan-picker="${ this.key }">
        ${ this.availableSellingPlanGroups ? this.availableSellingPlanGroups.map((group, index) => 
          html`
            <div class="skio-group-container skio-group-container--available ${ this.selectedSellingPlanGroup == group ? 'skio-group-container--selected' : '' }" skio-group-container>
              <div class="skio-group-topline">
                <label class="switch">
                  <input type="checkbox" @change="${ (e) => this.toggleSubscription(e, group) }">
                  <span class="slider round"></span>
                </label>

                <div class="skio-group-title">
                  ${ group.name == 'Subscription' ? 'Subscribe' : group.name}
                  ${ this.discount(group.selected_selling_plan).percent !== '0%' ? html`
                    <span> to Save <span skio-discount>${ this.discountFormat == 'percent' ? this.discount(group.selected_selling_plan).percent : this.discount(group.selected_selling_plan).amount }</span> more!</span>
                  ` : html`` }
                </div>
              </div>
              <div class="skio-group-content">
                <h6>Deliver Every:</h6>

                <div class="skio-button-selector-wrapper">
                  ${ group ? group.selling_plans.map((selling_plan) => 
                      html`
                      <div class="skio-button-selector">
                        <input type="checkbox" name="selling_plan_button-${ group.id }" id="selling_plan_button-${ selling_plan.id }" @change="${(e) => this.selectedSellingPlan = selling_plan }" .checked="${ this.selectedSellingPlan?.id == selling_plan.id }" />
                        <label for="selling_plan_button-${ selling_plan.id }">
                          ${ selling_plan.name.replace('Every', '') }
                        </label>
                      </div>
                      `
                    ): ''}
                </div>
              </div>
            </div>
          `
        ): ''}
      </fieldset>`
  }

  toggleSubscription = (e, group) => {
    if(e.target.checked) {
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = group.selected_selling_plan;
    } else {
      this.selectedSellingPlanGroup = null;
      this.selectedSellingPlan = null;
    }
  }

  updated = (changed) => {
    if(changed.has('product') && this.product) {
      //update key
      this.key = this.key ? this.key : this.product.id;

      //update skioSellingPlanGroups
      this.skioSellingPlanGroups = this.product.selling_plan_groups.filter(
        selling_plan_group => selling_plan_group.app_id === 'SKIO'
      )
    }

    if(changed.has('selectedVariant') && this.selectedVariant) {
      //update availableSellingPlanGroups based on skioSellingPlanGroups and selectedVariant.id
      this.availableSellingPlanGroups = this.skioSellingPlanGroups.filter(selling_plan_group =>
        selling_plan_group.selling_plans.some(selling_plan =>
          this.selectedVariant.selling_plan_allocations.some(
            selling_plan_allocation => selling_plan_allocation.selling_plan_id === selling_plan.id
          )
        )
      )

      //update selectedSellingPlan value
      if (this.availableSellingPlanGroups?.length > 0) {
        //update each group with a default selected_selling_plan
        this.availableSellingPlanGroups.forEach((group => {
          group.selected_selling_plan = group.selling_plans[0];
        }));

        if(this.selectedSellingPlanGroup || this.startSubscription == true || this.product.requires_selling_plan == true) {
          this.selectedSellingPlanGroup = this.availableSellingPlanGroups[0];
          this.selectedSellingPlan = this.selectedSellingPlanGroup.selling_plans[0];
        } else {
          this.selectedSellingPlan, this.selectedSellingPlanGroup = null
        }
      }

      this.updateForm();
    }

    if(changed.has('selectedSellingPlan')) {
      //update price of price elements if applicable
      document.querySelectorAll(`[skio-price][skio-key="${ this.key }"]`).forEach((el) => {
        el.innerHTML = this.price(this.selectedSellingPlan);
      });

      //update display of external content elements
      document.querySelectorAll(`[skio-onetime-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan !== null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      document.querySelectorAll(`[skio-subscription-content][skio-key="${ this.key }"]`).forEach((el) => {
        this.selectedSellingPlan == null ? el.style.display = "none" : el.style.removeProperty('display');
      });

      //dispatch CustomEvent to tell that this specific plan picker was updated, and pass the selectedSellingPlan
      const event = new CustomEvent(`skio::update-selling-plan`, {
        bubbles: true, 
        composed: true, 
        detail: {
          variant: this.selectedVariant,
          sellingPlan: this.selectedSellingPlan,
          key: this.key
        }
      });

      this.dispatchEvent(event);

      this.updateForm();
    }
  }

  log = (...args) => {
    args.unshift('%c[skio]', 'color: #8770f2;');
    console.log.apply(console, args);
  }

  error = (...args) =>  {
    args.unshift('%c [skio]', 'color: #ff0000');
    console.error.apply(console, args);
  }
  
  updateExternalPrice() {
    const price = this.selectedSellingPlan ? this.price(this.selectedSellingPlan) : this.money(this.selectedVariant.price);
    document.querySelectorAll(this.externalPriceSelector).forEach((el) => {
      el.innerHTML = price;
    })
    document.querySelectorAll(this.externalPriceSelectorWithCurrency).forEach((el) => {
      el.innerHTML = price + ' ' + this.currency;
    })

  }
  
  // Update selected selling plan group; called on click of skio-group-container element
  selectSellingPlanGroup(group) {
    this.selectedSellingPlanGroup = group;
    this.selectedSellingPlan = group?.selected_selling_plan;

    //update the form that was passed, if any
    this.updateForm();
  }

  // Update selected selling plan; called on change of skio-frequency select element
  selectSellingPlan(element, group) {
    let selling_plan = group.selling_plans.find(x => x.id == element.value);
    if (selling_plan) {
      group.selected_selling_plan = selling_plan;
      this.selectedSellingPlanGroup = group;
      this.selectedSellingPlan = selling_plan;
    }
    else this.log("Error: couldn't find selling plan with id " + element.value + " for variant " + this.selectedVariant.id + " from product " + this.product.id + " : " + this.product.handle);
  }

  // Formats integer value into money value
  money(price) {
    return this.moneyFormatter.format(price / 100.0)
  }

  // Calculates discount based on selling_plan.price_adjustments, returns { percent, amount } of selling plan discount
  discount(selling_plan) {
    if (!selling_plan)
      return { percent: '0%', amount: 0 }
    
    const price_adjustment = selling_plan.price_adjustments[0]
    const discount = { percent: '0%', amount: 0 }
    const price = this.selectedVariant.price;
    
    switch (price_adjustment.value_type) {
      case 'percentage':
        discount.percent = `${price_adjustment.value}%`
        discount.amount = Math.round(
          (price * price_adjustment.value) / 100.0
        )
        break
      case 'fixed_amount':
        discount.percent = `${Math.round(
          ((price_adjustment.value * 1.0) / price) * 100.0
        )}%`
        discount.amount = price_adjustment.value
        break
      case 'price':
        discount.percent = `${Math.round(
          (((price - price_adjustment.value) * 1.0) /
            price) *
            100.0
        )}%`
        discount.amount = price - price_adjustment.value
        break
    }
    
    return discount
  }

  // Calculates the variant's price for the given selling plan, returns a formatted money value (if desired)
  price(selling_plan, formatted = true) {
    return formatted
      ? this.money( this.selectedVariant.price - this.discount(selling_plan).amount)
      :  this.selectedVariant.price - this.discount(selling_plan).amount
  }

  // Updates element data to be registered by forms
  updateForm() {
    let $sellingPlan = this.querySelector('input[name=selling_plan]');
    if($sellingPlan) $sellingPlan .value = this.selectedSellingPlan ? this.selectedSellingPlan.id : '';

    let $discount = this.querySelector('input[name="properties[Discount]"]');
    if($discount) $discount.value = this.discount(this.selectedSellingPlan).percent;
    
    this.updateExternalPrice();
  }

  bindVariantChange() {
    let form = this.closest('form[action*="/cart/add"]');
    if(!this.formId && !form) return;

    const variantIdSelector = form ? form.querySelector('[name=id]') : document.querySelector(`#${ this.formId } [name=id]`);
  
    if(variantIdSelector) {
      variantIdSelector.addEventListener('change', (e) => {
        this.selectedVariant = this.product.variants.find(variant => variant.id == e.target.value);
      });
    }
  }

  // Optional functions keep if necessary 

  /**
   *   
   * 
   */
  
  // Runs a fetch request to add the selectedVariant to the cart with the passed quantity and selectedSellingPlan
  addToCart(quantity) {
    const items = [
      {
        id: this.selectedVariant.id,
        quantity: quantity,
        ...(this.selectedSellingPlan && { selling_plan: this.selectedSellingPlan?.id })
      }
    ];

    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ items })
    })
    .then((response) => response.json())
    .then((response) => {
      this.log("SKIO added item to cart: ", response);
      //dispatch CustomEvent to tell document that an item was added to cart
      const event = new CustomEvent(`skio::added-to-cart`, {
        bubbles: true, 
        composed: true, 
        detail: {
          response,
          key: this.key
        }
      });

      this.dispatchEvent(event);
    })
    .catch((error) => {
      this.error(`SKIO ${ this.key } error adding item to cart: `, error);
    });
  } 

  fetchProduct = (handle) => {
    return fetch(`/products/${ handle }.js`)
    .then((response) => response.json())
    .then((product) => {
      this.product = product;
      this.selectedVariant = product.variants[0];

      return product;
    });
  }
}

customElements.define('skio-plan-picker', SkioPlanPickerComponent);